// SF 監査実行 API クライアント。
// POST /sf/audit/run を呼び出してバックエンドから最新診断結果を取得する。
// ApiClient(frontend/src/api/client.ts) に乗せることで認証・エラー正規化を委譲する。
// toFindings は純粋関数 — API レスポンスをフロントエンドの Finding[] / Check[] に変換する。
import type { ApiClient } from "../api/client";
import { CHECKS, type Check, type Finding, type FindingStatus } from "./audit-data";

export interface AuditApiDeps {
  readonly apiClient: ApiClient;
}

// バックエンド /sf/audit/run のレスポンス型（backend R1–R22 設計書 Issue 2 対応）。
export interface RunAuditFinding {
  readonly checkId: string;
  readonly status: "pass" | "fail" | "na" | "review";
  readonly affectedCount: number;
  readonly affectedResources: string[];
  readonly detail?: {
    readonly orgValue?: string;
    readonly standardValue?: string;
  };
}

export interface RunAuditResponse {
  readonly healthCheckScore: number;
  readonly findings: RunAuditFinding[];
  readonly coverage: {
    readonly evaluated: string[];
    readonly notEvaluated: string[];
  };
  readonly runAt: string;
  readonly orgId: string;
  readonly instanceUrl: string;
}

// runAudit のオプション。persist=true の時のみ履歴に永続化される（backend は body で分岐）。
// 省略 / false は read-only（診断結果は返るがランは保存されない）。表示用呼び出しは read-only に保つ。
export interface RunAuditOptions {
  readonly persist?: boolean;
}

// POST /sf/audit/run → RunAuditResponse
// opts.persist === true の時だけボディに { persist: true } を送り履歴へ保存させる（R: ラン量産防止）。
export async function runAudit(deps: AuditApiDeps, opts?: RunAuditOptions): Promise<RunAuditResponse> {
  return deps.apiClient.post<RunAuditResponse>("/sf/audit/run", opts?.persist === true ? { persist: true } : {});
}

// 純粋ヘルパー: API レスポンスをフロントエンドの Finding[] と未評価 Check[] に変換する。
// checkId が CHECKS カタログに存在しない場合は無視（防御的スキップ）。
export function toFindings(resp: RunAuditResponse): { findings: Finding[]; notEvaluated: Check[] } {
  const checkMap = new Map<string, Check>(CHECKS.map((c) => [c.id, c]));

  const findings: Finding[] = [];
  for (const rf of resp.findings) {
    const check = checkMap.get(rf.checkId);
    if (check === undefined) continue; // 未知 checkId は防御的スキップ
    const finding: Finding = {
      check,
      status: rf.status as FindingStatus,
      affectedCount: rf.affectedCount,
      affectedResources: rf.affectedResources,
      lastDetected: resp.runAt,
    };
    findings.push(finding);
  }

  const notEvaluated: Check[] = [];
  for (const id of resp.coverage.notEvaluated) {
    const check = checkMap.get(id);
    if (check === undefined) continue; // 未知 id は防御的スキップ
    notEvaluated.push(check);
  }

  return { findings, notEvaluated };
}
